import { compose } from 'redux';
import isNil from 'lodash/isNil';
import { useEffect, useCallback, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { CircularProgress } from '@material-ui/core/index';
import PlusIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import DoneIcon from '@material-ui/icons/Done';
import { green } from '@material-ui/core/colors';
import FilterList from '@material-ui/icons/FilterList';
import withStyles from '@material-ui/styles/withStyles';
import { Grid, Typography, Button, Box } from '@material-ui/core';

import { useToggle } from 'altus-hooks';
import { BasePage } from 'altus-ui-components';
import { getSummarizedDataStateFromState } from 'app/app.selectors';
import {
  getIntegrityManagementAvailableFilesState,
  getIntegrityManagementIntegritiesFromStateByRiskPrinciple,
  getIntegrityManagementWellIntegrityAttachmentsFromState,
} from 'features/settings/integrityManagement/helpers/integrityManagement.selectors';
import {
  INTEGIRTY_ATTACHMENT_SOURCE_TYPE_ENUM,
  INTEGRITY_MANAGEMENT_ACTIONS,
  RISK_PRINCIPLE_TYPES,
} from 'features/settings/integrityManagement/helpers/integrityManagement.constants';
import {
  loadIntegrityManagementAvailableFiles,
  loadIntegrityManagementIntegritiesForWell,
  loadIntegrityManagementWellIntegrityAttachments,
  previewIntegrityManagementProjectDocument,
  previewIntegrityManagementWellboreDocument,
  updateIntegrityManagementWellAttachments,
  uploadAndUpdateIntegrityManagementWellAttachments,
} from 'features/settings/integrityManagement/helpers/integrityManagement.actions';

import useWellbore from 'features/wells/hooks/useWellbore';
import IntegrityAttachmentsTable from 'features/wells/integrity/attachments/IntegrityAttachmentsTable';
import { EMPTY_LIST } from 'app/app.constants';
import UploadIntegrityAttachmentDrawer from 'features/wells/integrity/attachments/upload/UploadIntegrityAttachmentDrawer';
import IntegrityAttachmentsPreview from 'features/wells/integrity/attachments/IntegrityAttachmentPreview';
import IntegrityFilterAttachmentDrawer from 'features/wells/integrity/attachments/filter/IntegrityFilterAttachmentDrawer';
import Badge from '@material-ui/core/Badge';

export const DEFAULT_ATTACHMENT_FILTERS = {
  fileTypes: {},
  years: {},
};

const IntegrityAttachmentsContainer = ({
  classes,
  dataState,
  wellboreId,
  integrityId,
  attachments,
  availableFiles,
  dispatchLoadWellIntegrityAttachments,
  dispatchLoadIntegrityManagementForWell,
  dispatchUpdateWellIntegrityAttachments,
  dispatchLoadWellIntegrityAvailableFiles,
  dispatchUploadAndUpdateWellIntegrityAttachments,
}) => {
  const dispatch = useDispatch();

  const [isOpen, toggle] = useToggle();
  const [isFilterOpen, toggleFilterDrawer] = useToggle();
  const { wellbore } = useWellbore(wellboreId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
  const [externalWellboreId, setExternalWellboreId] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState({
    id: '',
    fileName: '',
    filePath: '',
    fileType: '',
  });

  const [wellboreFiles, setWellboreFiles] = useState(EMPTY_LIST);
  const [projectFiles, setProjectFiles] = useState(EMPTY_LIST);
  const [loadingAvailableFiles, setLoadingAvailableFiles] = useState(true);
  const availableFilesForPreviewRef = useRef(EMPTY_LIST);

  useEffect(
    () => {
      if (wellbore.get('externalWellboreExternalId')) {
        dispatchLoadIntegrityManagementForWell(
          wellbore.get('externalWellboreExternalId'),
        );
        setExternalWellboreId(wellbore.get('externalWellboreExternalId'));

        if (integrityId) {
          dispatchLoadWellIntegrityAttachments(
            wellbore.get('externalWellboreExternalId'),
            integrityId,
          );
          dispatchLoadWellIntegrityAvailableFiles(
            wellbore.get('externalWellboreExternalId'),
          );
        }
      }
    }, // eslint-disable-next-line
    [wellbore, integrityId],
  );

  useEffect(() => {
    if (availableFiles && availableFiles !== EMPTY_LIST) {
      availableFilesForPreviewRef.current = availableFiles;
      if (attachments && attachments !== EMPTY_LIST) {
        const attachmentsExternalIds = attachments.map(
          (item) => item.externalFileId,
        );

        const wellboreList = availableFiles
          .filter((item) => item.wellboreId !== null)
          .map((item) => ({
            ...item,
            isChecked: attachmentsExternalIds.includes(item.externalId),
            fileSource: INTEGIRTY_ATTACHMENT_SOURCE_TYPE_ENUM.WELL,
          }));

        setWellboreFiles(wellboreList);

        const projectList = availableFiles
          .filter((item) => item.projectId && item.projectId !== 0) // Filter valid items
          .map((item) => ({
            ...item,
            isChecked: attachmentsExternalIds.includes(item.externalId),
            fileSource: INTEGIRTY_ATTACHMENT_SOURCE_TYPE_ENUM.PROJECT,
          }))
          .groupBy((item) => item.projectId); // Group by `projectId`

        setProjectFiles(projectList);
      }
      // this is for the case we have an empty well integrity rule - with no attachments at all
      else {
        const wellboreList = availableFiles
          .filter((item) => item.wellboreId !== null)
          .map((item) => ({
            ...item,
            isChecked: false,
            fileSource: INTEGIRTY_ATTACHMENT_SOURCE_TYPE_ENUM.WELL,
          }));

        setWellboreFiles(wellboreList);

        const projectList = availableFiles
          .filter((item) => item.projectId && item.projectId !== 0) // Filter items with projectId - those are from project
          .map((item) => ({
            ...item,
            isChecked: false,
            fileSource: INTEGIRTY_ATTACHMENT_SOURCE_TYPE_ENUM.PROJECT,
          }))
          .groupBy((item) => item.projectId); // Group by `projectId`

        setProjectFiles(projectList);
      }
    }
    setLoadingAvailableFiles(false);
  }, [availableFiles, attachments]);

  const handleUpdateAttachments = (newAttachments, newFiles, deletion) => {
    setSelectedAttachment({
      id: '',
      filePath: '',
      fileName: '',
      fileType: '',
    });
    // if there are any files call update of the files
    if (newFiles.length > 0) {
      dispatchUploadAndUpdateWellIntegrityAttachments(
        externalWellboreId,
        integrityId,
        newAttachments,
        newFiles,
        toggle,
      );
    } else if (deletion) {
      dispatchUpdateWellIntegrityAttachments(
        externalWellboreId,
        integrityId,
        newAttachments,
      );
    } else {
      dispatchUpdateWellIntegrityAttachments(
        externalWellboreId,
        integrityId,
        newAttachments,
        toggle,
      );
    }
  };

  // dispatch will close the modal, enable editing on repopen
  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // attachment filters
  const [filteredAttachments, setFilteredAttachments] = useState(EMPTY_LIST);
  const [filters, setFilters] = useState(DEFAULT_ATTACHMENT_FILTERS);
  const [filtersSelected, setFiltersSelected] = useState({
    selected: false,
    count: 0,
  });

    const countSelectedFilters = () => {
    let count = 0;

    Object.values(filters.fileTypes).forEach((isSelected) => {
      if (isSelected) count++;
    });

    Object.values(filters.years).forEach((year) => {
      if (year.isSelected) count++;
      Object.values(year.months).forEach((isSelected) => {
        if (isSelected) count++;
      });
    });

    return count;
  };

  const hasSelectedFilters = () => {
    const fileTypeSelected = Object.values(filters.fileTypes).some((v) => v);
    const yearSelected = Object.values(filters.years).some(
      (year) => year.isSelected || Object.values(year.months).some((v) => v),
    );

    return fileTypeSelected || yearSelected;
  };

  useEffect(
    () => {
      const isYearEffectivelySelected = (y) => {
        if (!y) return false;
        const months = y.months || {};
        const anyMonthSelected = Object.values(months).some(Boolean);
        return y.selected && !anyMonthSelected;
      };

      const updateFilteredAttachments = () => {
        const { fileTypes, years } = filters;

        const newFilteredAttachments = attachments.filter((attachment) => {
          const fileType = attachment.fileType;
          const reportedDate = attachment.reportedDate;

          const date = new Date(reportedDate);
          const year = date.getFullYear();
          const month = date.toLocaleString('default', { month: 'long' });

          const isFileTypeSelected = !!fileTypes[fileType];
          const y = years[year];
          const isMonthSelected = !!y?.months?.[month];
          const yearSelectedNoMonths = isYearEffectivelySelected(y);

          return (
            isFileTypeSelected && (isMonthSelected || yearSelectedNoMonths)
          );
        });

        setFilteredAttachments(newFilteredAttachments);
      };

      if (filters !== DEFAULT_ATTACHMENT_FILTERS) {
        updateFilteredAttachments();
      }
      const countSelectedFilters = () => {
        const { fileTypes, years } = filters;

        let count = Object.values(fileTypes).filter(Boolean).length;

        Object.values(years).forEach((y) => {
          if (!y) return;
          const months = y.months || {};
          const selectedMonths = Object.values(months).filter(Boolean).length;

          if (selectedMonths > 0) {
            count += selectedMonths;
          } else if (y.selected) {
            count += 1;
          }
        });

        return count;
      };
      const hasSelectedFilters = () => countSelectedFilters() > 0;
      setFiltersSelected((prevFiltersSelected) => ({
        ...prevFiltersSelected,
        selected: hasSelectedFilters(),
        count: countSelectedFilters(),
      }));
    },
    // eslint-disable-next-line
    [attachments, filters],
  );

  useEffect(() => {
    const initializeFilters = () => {
      const fileTypes = {};
      const years = {};

      // Extract unique fileTypes and group dates
      attachments.forEach((attachment) => {
        const { fileType, reportedDate } = attachment;

        // Initialize fileType filters
        if (!fileTypes[fileType]) {
          fileTypes[fileType] = true; // Default to true since all filters should be on by default
        }

        // Group reportedDate by year and month
        const date = new Date(reportedDate);
        const year = date.getFullYear();
        const month = date.toLocaleString('default', { month: 'long' });

        if (!years[year]) {
          years[year] = {
            isSelected: true, // Default to true since all filters should be on by default
            months: {},
          };
        }

        if (!years[year].months[month]) {
          years[year].months[month] = true; // Default to true since all filters should be on by default
        }
      });

      setFilters({ fileTypes, years });
    };

    initializeFilters();
    setFilteredAttachments(attachments);
  }, [attachments]);

  const handlePreviewAttachment = useCallback(
    (attachment) => {
      if (attachment.externalFileId === selectedAttachment.id) {
        setSelectedAttachment({
          id: '',
          filePath: '',
          fileName: '',
          fileType: '',
        });
        setIsAttachmentLoading(false);
      } else if (
        attachment.fileSource === INTEGIRTY_ATTACHMENT_SOURCE_TYPE_ENUM.PROJECT
      ) {
        const file = availableFilesForPreviewRef.current.find(
          (projectfile) => attachment.externalFileId === projectfile.externalId,
        );
        dispatch(
          previewIntegrityManagementProjectDocument(
            file.projectId,
            file.fileId,
          ),
        ).then((response) => {
          setSelectedAttachment({
            id: file.externalId,
            filePath: response.previewUrl,
            fileName: file.fullName,
            fileType: file.extension?.replace('.', ''),
          });
          setIsAttachmentLoading(false);
        });
      } else if (
        attachment.fileSource === INTEGIRTY_ATTACHMENT_SOURCE_TYPE_ENUM.WELL
      ) {
        const file = availableFilesForPreviewRef.current.find(
          (wellborefile) =>
            attachment.externalFileId === wellborefile.externalId,
        );
        dispatch(
          previewIntegrityManagementWellboreDocument(
            wellbore?.get('fieldId'),
            file.wellboreId,
            file.fileId,
          ),
        ).then((response) => {
          setSelectedAttachment({
            id: file.externalId,
            filePath: response.previewUrl,
            fileName: file.fullName,
            fileType: file.extension?.replace('.', ''),
          });
          setIsAttachmentLoading(false);
        });
      }
    },
    [availableFilesForPreviewRef, dispatch, selectedAttachment.id, wellbore],
  );

  const resetPreviewState = () => {
    setSelectedAttachment({
      id: '',
      filePath: '',
      fileName: '',
      fileType: '',
    });
  };

  return (
    <BasePage dataState={dataState}>
      <Grid item container spacing={2} className={classes.tableContainer}>
        {externalWellboreId && (
          <Grid item spacing={2} xs={12} className={classes.container}>
            <Grid item xs={6} spacing={2}>
              <Grid item xs={12} container>
                <Grid item xs={8} className={classes.statusText}>
                  <Box display="flex" justifyContent="flex-start">
                    {attachments?.size > 0 && (
                      <Typography>
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <DoneIcon className={classes.successIcon} />
                          {'Rule compliant. Document(s) uploaded'}
                        </span>
                      </Typography>
                    )}
                    {attachments?.size === 0 && (
                      <Typography align="right" nowrap>
                        <InfoIcon color="primary" fontSize="small" />
                        {'Rule not compliant. Document(s) missing'}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={4} className={classes.actionButton}>
                  <Button
                    title="Upload document"
                    color="primary"
                    variant="contained"
                    disabled={loadingAvailableFiles}
                    onClick={toggle}
                    startIcon={
                      loadingAvailableFiles ? (
                        <CircularProgress size={14} color={'white'} />
                      ) : (
                        <PlusIcon fontSize="small" />
                      )
                    }
                  >
                    Upload document
                  </Button>
                </Grid>
              </Grid>
              {attachments && attachments !== EMPTY_LIST && (
                <Grid item container xs={12}>
                  <Grid item xs={6}>
                    <Typography align="left" nowrap>
                      {'Supporting document(s)'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        title="Compare"
                        color="secondary"
                        variant="contained"
                        onClick={toggleFilterDrawer}
                        className={classes.transparentButton}
                        startIcon={
                          filtersSelected.selected ? (
                            <Badge
                              overlap="circular"
                              color="primary"
                              badgeContent={filtersSelected.count}
                              invisible={
                                !filtersSelected.selected ||
                                !filtersSelected.count
                              }
                              max={99}
                              anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                              }}
                              className={classes.filtersBadge}
                            >
                              <FilterList color="action" fontSize="medium" />
                            </Badge>
                          ) : (
                            <FilterList color="action" fontSize="medium" />
                          )
                        }
                      >
                        Filters
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                {attachments &&
                attachments !== EMPTY_LIST &&
                availableFiles !== EMPTY_LIST ? (
                  <IntegrityAttachmentsTable
                    attachments={filteredAttachments}
                    allAttachments={attachments}
                    handlePreviewAttachment={handlePreviewAttachment}
                    setIsLoading={setIsAttachmentLoading}
                    handleUpdateAttachments={handleUpdateAttachments}
                  />
                ) : (
                  attachments !== EMPTY_LIST && (
                    <Grid className={classes.circularProgress}>
                      <CircularProgress size={40} />
                    </Grid>
                  )
                )}
              </Grid>
            </Grid>
            <Grid item xs={6} className={classes.preview}>
              {!isNil(selectedAttachment.id) && (
                <IntegrityAttachmentsPreview
                  selectedAttachment={selectedAttachment}
                  isLoading={isAttachmentLoading}
                  resetPreviewState={resetPreviewState}
                />
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
      <UploadIntegrityAttachmentDrawer
        isOpen={isOpen}
        toggleDrawer={toggle}
        attachments={attachments}
        wellboreFiles={wellboreFiles}
        projectFiles={projectFiles}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        handleUpdateAttachments={handleUpdateAttachments}
        fieldId={wellbore?.get('fieldId')}
      />
      <IntegrityFilterAttachmentDrawer
        isOpen={isFilterOpen}
        toggleDrawer={toggleFilterDrawer}
        filters={filters}
        setFilters={setFilters}
      />
    </BasePage>
  );
};

const styles = (theme) => ({
  tableContainer: {
    padding: theme.spacing(2),
    height: '100%',
  },
  preview: {
    padding: theme.spacing(1),
    marginLeft: theme.spacing(2),
    height: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
  },
  successIcon: {
    color: green[600],
    marginRight: theme.spacing(1),
  },
  statusText: {
    padding: theme.spacing(2),
  },
  transparentButton: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: 'inherit',
  },
  circularProgress: {
    display: 'flex',
    justifyContent: 'center',
  },
  actionButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  filtersBadge: {
    fontSize: theme.spacing(1),
    height: theme.spacing(2),
    minWidth: theme.spacing(2),
    borderRadius: '50%',
  },
});

export default compose(
  connect(
    (state) => ({
      filteredIntegrities:
        getIntegrityManagementIntegritiesFromStateByRiskPrinciple(
          state,
          RISK_PRINCIPLE_TYPES.LIKELIHOOD_OF_FAILURE,
        ),
      dataState: getSummarizedDataStateFromState(
        state,
        INTEGRITY_MANAGEMENT_ACTIONS.GET_ALL_INTEGRITY_MANAGEMENT_INTEGRITIES_FOR_WELL,
      ),
      attachments: getIntegrityManagementWellIntegrityAttachmentsFromState(
        state,
        INTEGRITY_MANAGEMENT_ACTIONS.GET_ALL_INTEGRITY_MANAGEMENT_WELL_INTEGRITY_ATTACHMENTS,
      ),
      availableFiles: getIntegrityManagementAvailableFilesState(
        state,
        INTEGRITY_MANAGEMENT_ACTIONS.GET_ALL_INTEGRITY_MANAGEMENT_AVAILABLE_FILES,
      ),
    }),
    {
      dispatchLoadIntegrityManagementForWell:
        loadIntegrityManagementIntegritiesForWell,
      dispatchLoadWellIntegrityAttachments:
        loadIntegrityManagementWellIntegrityAttachments,
      dispatchLoadWellIntegrityAvailableFiles:
        loadIntegrityManagementAvailableFiles,
      dispatchUpdateWellIntegrityAttachments:
        updateIntegrityManagementWellAttachments,
      dispatchUploadAndUpdateWellIntegrityAttachments:
        uploadAndUpdateIntegrityManagementWellAttachments,
    },
  ),
  withStyles(styles),
)(IntegrityAttachmentsContainer);
